import { cloneElement, isValidElement, useCallback, useEffect, useRef, type FocusEvent as ReactFocusEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode } from 'react'
import { setMergedRef } from './hoverRefHelpers'
import { useLongPress } from '../../../hooks/useLongPress'

import { HoverBubble } from './HoverBubble'
import { useHover } from './useHover'
import type { HoverContent, HoverOptions } from './hoverTypes'

interface HoverableProps extends HoverOptions {
  content: HoverContent
  children: ReactElement<Record<string, unknown>> | ReactNode
  longPressMs?: number
  onAnchorRef?: (node: HTMLElement | null) => void
}

const TOUCH_QUERY = '(hover: none)'

function ClonedChild({
  child,
  onAnchorRef,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
  onClick,
  onFocus,
  onBlur,
  onKeyDown,
}: {
  child: ReactElement<Record<string, unknown>>
  onAnchorRef: (node: HTMLElement | null) => void
  onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerLeave: () => void
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onClick: () => void
  onFocus: (event: ReactFocusEvent<HTMLElement>) => void
  onBlur: () => void
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void
}) {
  return cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      const existing = (child.props as { ref?: unknown })?.ref ?? (child as { ref?: unknown }).ref
      setMergedRef(node, onAnchorRef, existing)
    },
    onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
      onPointerEnter(event)
      const orig = child.props?.onPointerEnter as ((e: ReactPointerEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => {
      onPointerLeave()
      const orig = child.props?.onPointerLeave as ((e: ReactPointerEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
      onPointerDown(event)
      const orig = child.props?.onPointerDown as ((e: ReactPointerEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => {
      onPointerUp()
      const orig = child.props?.onPointerUp as ((e: ReactPointerEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => {
      onPointerCancel()
      const orig = child.props?.onPointerCancel as ((e: ReactPointerEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      onClick()
      const orig = child.props?.onClick as ((e: ReactMouseEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      onFocus(event)
      const orig = child.props?.onFocus as ((e: ReactFocusEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onBlur: (event: ReactFocusEvent<HTMLElement>) => {
      onBlur()
      const orig = child.props?.onBlur as ((e: ReactFocusEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      onKeyDown(event)
      const orig = child.props?.onKeyDown as ((e: ReactKeyboardEvent<HTMLElement>) => void) | undefined
      orig?.(event)
    },
  })
}

export function Hoverable({
  content,
  children,
  variant = 'label',
  placement = 'auto',
  delay,
  speed,
  group,
  avoidZonesGroup,
  disabled = false,
  maxWidth,
  interactive = false,
  offset,
  bubbleClassName,
  longPressMs = 450,
  onAnchorRef,
}: HoverableProps) {
  const hover = useHover({ variant, placement, delay, speed, group, avoidZonesGroup, disabled, maxWidth, interactive, offset, bubbleClassName })
  const anchorRef = useRef<HTMLElement | null>(null)
  const handleAnchorRef = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node
    onAnchorRef?.(node)
  }, [onAnchorRef])
  const longPress = useLongPress({ ms: longPressMs, disabled, onLongPress: () => {
    const el = anchorRef.current
    if (el) hover.show({ anchor: el, content })
  } })

  const onPointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') return
      const el = event.currentTarget as HTMLElement
      hover.show({ anchor: el, content })
    },
    [hover, content],
  )

  const onPointerLeave = useCallback(() => {
    hover.hide()
  }, [hover])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => { if (event.pointerType === 'touch') longPress.onPointerDown(event) }, [longPress])

  const onFocus = useCallback(
    (event: ReactFocusEvent<HTMLElement>) => {
      const target = event.target as Element
      let isFocusVisible = false
      try {
        if (typeof target.matches === 'function' && target.matches(':focus-visible')) {
          isFocusVisible = true
        }
      } catch {
        // ignore
      }
      if (isFocusVisible) {
        const el = event.currentTarget as HTMLElement
        hover.show({ anchor: el, content, immediate: true })
      }
    },
    [hover, content],
  )

  const onBlur = useCallback(() => {
    hover.hide()
  }, [hover])

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        hover.hide()
      }
    },
    [hover],
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(TOUCH_QUERY)
    if (!media.matches) return
    const onOutside = (event: TouchEvent | MouseEvent) => {
      if (!anchorRef.current) return
      const target = event.target as Node | null
      if (target && anchorRef.current.contains(target)) return
      hover.hide()
    }
    document.addEventListener('touchstart', onOutside, { passive: true })
    document.addEventListener('mousedown', onOutside, true)
    return () => {
      document.removeEventListener('touchstart', onOutside)
      document.removeEventListener('mousedown', onOutside, true)
    }
  }, [hover])

  const child = isValidElement(children) ? (children as ReactElement<Record<string, unknown>>) : null

  const inner = child ? (
    <ClonedChild
      child={child}
      onAnchorRef={handleAnchorRef}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerUp={longPress.onPointerUp}
      onPointerCancel={() => {
        longPress.onPointerCancel()
        hover.hide()
      }}
      onClick={hover.hide}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  ) : (
    <span
      ref={handleAnchorRef}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerUp={longPress.onPointerUp}
      onPointerCancel={() => {
        longPress.onPointerCancel()
        hover.hide()
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {children}
    </span>
  )

  return (
    <>
      {inner}
      {disabled ? null : <HoverBubble {...hover.bubbleProps} />}
    </>
  )
}
